import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useComponents } from 'client/common/hooks';
import cn from 'utils/cn';
import { getCookie, setCookieWithAge } from 'utils/cookies';
import { reachGoal } from 'utils/metrika';

import { IAdCardProps } from './AdCard.types';

import './AdCard.css';

const b = cn('ad-card');

export default function AdCard({ data, open, close }: IAdCardProps) {
    const { HtmlContent } = useComponents();
    const cookieName = `popup-${data.slug}`;
    const sessionCookieName = `${cookieName}-session`;
    const closeTimeoutId = useRef<NodeJS.Timeout | null>(null);

    const [isClosingEnabled, setIsClosingEnabled] = useState(false);
    const enableClosing = useCallback(() => {
        setIsClosingEnabled(true);
        setCookieWithAge(cookieName, 'incomplete', 86400 * 14);
    }, [cookieName]);

    const buttonClick = useCallback(() => {
        if (data.metrikaGoalId) {
            reachGoal(data.metrikaGoalId, {
                event: 'go-to-link',
                slug: data.slug
            });
        }
        if (closeTimeoutId.current) {
            clearTimeout(closeTimeoutId.current);
        }
        setCookieWithAge(cookieName, 'complete', 34560000);
        close();
    }, [closeTimeoutId, close, cookieName, data.metrikaGoalId, data.slug]);

    const onClose = useCallback(() => {
        if (data.metrikaGoalId) {
            reachGoal(data.metrikaGoalId, {
                event: 'click-on-close-button',
                slug: data.slug
            });
        }
        close();
    }, [close, data.metrikaGoalId, data.slug]);

    useEffect(() => {
        if (getCookie(cookieName)) {
            close();
        } else {
            open();
            closeTimeoutId.current = setTimeout(enableClosing, 3000);
        }
    }, [close, cookieName, enableClosing, open, sessionCookieName]);

    return (
        <div className={b()}>
            { isClosingEnabled && <div className={b('close')} onClick={onClose} />}
            <div className={b('info')}>
                <HtmlContent className={b('text', { type: 'title' })} content={data.title} />
                <HtmlContent className={b('text', { type: 'description' })} content={data.description} />
                <div className={b('button')}>
                    <a href={data.button.href} target="_blank" rel="noopener noreferrer" onClick={buttonClick}>
                        <img src={data.button.image} />
                        { data.button.title }
                    </a>
                </div>
            </div>
            <img src={data.image} />
        </div>
    );
}
